import { useLingui } from "@lingui/react/macro";
import { FormControl, FormError, LinkButton, useControl } from "@offstreetparking/design-system/forms";
import { FileUpload, type FileUploadFile } from "@offstreetparking/design-system/forms/FileUpload";
import { FileIcon } from "@offstreetparking/design-system/icons/FileIcon";
import { Text } from "@offstreetparking/design-system/typography";
import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { offstreetClient } from "../../../../api/client";
import { usePrivateFileUploader } from "./usePrivateFileUploader";

const MAX_FILE_SIZE = 2_000_000;

type FileUploadFieldProps = {
	index: number;
	label: string;
	formHelperText?: string | null;
	isRequired: boolean;
	companyId: number;
};

export const FileUploadField = ({ index, label, formHelperText, isRequired, companyId }: FileUploadFieldProps) => {
	const { t } = useLingui();
	const { setValue, watch } = useFormContext();
	const { error, field } = useControl({ name: `fields.${index}.value` });
	const fileName = watch(`fields.${index}.fileName`);
	const [uploadingFiles, setUploadingFiles] = useState<FileUploadFile[]>([]);
	const uploader = usePrivateFileUploader({ companyId });
	const pendingFileId = Number(field.value) || undefined;

	const { data: pendingFile } = offstreetClient.fileUploads.getPendingFileStatus.useQuery(
		{ pendingFileId: pendingFileId! },
		{
			enabled: !!pendingFileId,
			refetchInterval: (query) => {
				const status = query.state.data?.status;
				return status === "PROMOTED" || status === "REJECTED" || status === "SCAN_FAILED" ? false : 2_000;
			},
		},
	);
	const isRejected = pendingFile?.status === "REJECTED" || pendingFile?.status === "SCAN_FAILED";
	const isPromoted = pendingFile?.status === "PROMOTED";

	const onAddFiles = async (files: FileUploadFile[]) => {
		const [uploadingFile] = files;
		if (!uploadingFile) {
			return;
		}
		setUploadingFiles([uploadingFile]);
		if (uploadingFile.error || !uploadingFile.file) {
			return;
		}
		try {
			const uploadedFile = await uploader.upload(uploadingFile.file, (progress) =>
				setUploadingFiles([{ ...uploadingFile, progress }]),
			);
			setValue(`fields.${index}.fileName`, uploadedFile.name);
			field.onChange(String(uploadedFile.pendingFileId));
			setUploadingFiles([]);
		} catch {
			setUploadingFiles([{ ...uploadingFile, error: t`Upload failed`, progress: 100 }]);
		}
	};

	if (pendingFileId) {
		return (
			<FormControl
				name={field.name}
				label={label}
				isRequired={isRequired}
				formHelperText={formHelperText}
				error={error}
			>
				<div className="flex items-center justify-between gap-2 rounded-lg border border-neutral-faded bg-white p-2">
					<div className="flex min-w-0 items-center gap-2">
						<FileIcon className="h-5 min-h-5 w-5 min-w-5" />
						<Text size="sm" className="truncate">
							{fileName}
						</Text>
						{!isPromoted && !isRejected && (
							<Text size="sm" intent="muted" className="whitespace-nowrap">
								{t({
									message: "Scanning…",
									comment:
										"Shown beside an uploaded file while it is checked for viruses before it can be submitted",
								})}
							</Text>
						)}
					</div>
					<LinkButton
						type="button"
						size="sm"
						onClick={() => {
							setValue(`fields.${index}.fileName`, undefined);
							field.onChange("");
						}}
					>
						{t`Remove`}
					</LinkButton>
				</div>
				{isRejected && (
					<FormError
						error={t({
							message: "This file did not pass the security scan. Remove it and upload a different file.",
							comment: "Error under an uploaded file that the virus scan rejected",
						})}
					/>
				)}
			</FormControl>
		);
	}

	return (
		<FileUpload
			name={field.name}
			label={label}
			isRequired={isRequired}
			formHelperText={formHelperText}
			error={error}
			acceptedFileTypes={["image", "pdf"]}
			maxFileSize={MAX_FILE_SIZE}
			files={uploadingFiles}
			onAddFiles={onAddFiles}
			onRemoveFile={() => setUploadingFiles([])}
		/>
	);
};
